const SUPPORTS_PASSIVE = (() => {
  let supported = false;
  try {
    const options = {
      get passive() {
        supported = true;
        return true;
      },
    };
    window.addEventListener('test', null, options);
    window.removeEventListener('test', null, options);
  } catch (_) {
    /* ignore */
  }
  return supported;
})();

window.theme = window.theme || {};
window.theme.supportsPassive = SUPPORTS_PASSIVE;

/**
 * Global vertical swipe / pan gestures.
 * Drop-in: wrap any drawer header in <swipe-gesture> — swipedown dismisses the parent drawer.
 * Dispatches: gesture:panstart | panmove | panend | swipeup | swipedown | longpress
 */
export class SwipeGesture extends HTMLElement {
  constructor() {
    super();

    this._listeners = [];
    this.config = {
      thresholdY: 48,
      velocityThreshold: 0.4,
      disregardVelocityThresholdY: 80,
      pressThreshold: 8,
      diagonalSwipes: false,
      diagonalLimit: Math.tan(((45 * 1.5) / 180) * Math.PI),
      longPressTime: 500,
    };
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.teardown();
    this.initialized = false;
  }

  on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this._listeners.push({ target, type, handler, options });
  }

  teardown() {
    clearTimeout(this.longPressTimer ?? undefined);
    this._listeners.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options);
    });
    this._listeners = [];
  }

  init() {
    if (this.initialized) return;

    this.initialized = true;
    const options = window.theme.supportsPassive ? { passive: true } : false;

    this.on(this, 'touchstart', this.onTouchStart.bind(this), options);
    this.on(this, 'touchmove', this.onTouchMove.bind(this), options);
    this.on(this, 'touchend', this.onTouchEnd.bind(this), options);
    this.on(this, 'gesture:swipedown', this.dismissDrawer.bind(this));
  }

  get drawer() {
    return this.closest('[data-drawer], [data-popup], .drawer, coco-cart-drawer');
  }

  dismissDrawer() {
    const drawer = this.drawer;
    if (!drawer?.hasAttribute('active')) return;

    if (typeof drawer.close === 'function') {
      drawer.close();
      return;
    }

    if (typeof drawer.hide === 'function') {
      drawer.hide();
      return;
    }

    const closeBtn =
      drawer.querySelector('[is="close-drawer"]:not(.fixed-overlay):not([data-fixed-overlay])') ||
      drawer.querySelector('[is="close-drawer"]');
    closeBtn?.click();
  }

  fire(type, event) {
    this.dispatchEvent(
      new CustomEvent(`gesture:${type}`, {
        bubbles: true,
        composed: true,
        detail: {
          originalEvent: event,
        },
      })
    );
  }

  onTouchStart(event) {
    const touch = event.changedTouches[0];
    this.thresholdY = this.config.thresholdY;
    this.disregardVelocityThresholdY = Math.max(
      this.config.disregardVelocityThresholdY,
      Math.floor(0.5 * this.clientHeight) || 0,
    );
    this.touchStartY = touch.screenY;
    this.touchStartTime = Date.now();
    this.touchMoveY = null;
    this.touchEndY = null;
    this.velocityY = 0;
    this.swipingDirection = null;
    this.longPressTimer = setTimeout(
      () => this.fire('longpress', event),
      this.config.longPressTime
    );
    this.fire('panstart', event);
  }

  onTouchMove(event) {
    const touchMoveY = event.changedTouches[0].screenY - (this.touchStartY ?? 0);
    this.touchMoveY = touchMoveY;

    const absTouchMoveY = Math.abs(this.touchMoveY);
    this.swipingVertical = absTouchMoveY > this.thresholdY;
    this.swipingDirection = this.swipingVertical ? 'vertical' : 'pre-vertical';

    if (absTouchMoveY > this.config.pressThreshold) {
      clearTimeout(this.longPressTimer ?? undefined);
    }

    this.fire('panmove', event);
  }

  onTouchEnd(event) {
    this.touchEndY = event.changedTouches[0].screenY;
    this.fire('panend', event);
    clearTimeout(this.longPressTimer ?? undefined);

    const y = this.touchEndY - (this.touchStartY ?? 0);
    const absY = Math.abs(y);
    const elapsed = Math.max(1, Date.now() - (this.touchStartTime ?? Date.now()));
    this.velocityY = y / elapsed;

    if (absY <= this.thresholdY) return;

    this.swipedVertical = this.config.diagonalSwipes
      ? y <= this.config.diagonalLimit
      : absY > this.thresholdY;

    if (!this.swipedVertical) return;

    if (y < 0) {
      if (
        this.velocityY < -this.config.velocityThreshold ||
        y < -this.disregardVelocityThresholdY
      ) {
        this.fire('swipeup', event);
      }
    } else if (
      this.velocityY > this.config.velocityThreshold ||
      y > this.disregardVelocityThresholdY
    ) {
      this.fire('swipedown', event);
    }
  }
}

if (!customElements.get('swipe-gesture')) {
  customElements.define('swipe-gesture', SwipeGesture);
}
